/**
 * Stone Castle — light / dark theme toggle (Horizon + teset.css)
 *
 * theme.liquid — inside <head>:
 *
 * <script>
 *   (function () {
 *     var s = localStorage.getItem('sc-theme');
 *     var t = s === 'dark' || s === 'light' ? s : 'light';
 *     var r = document.documentElement;
 *     r.setAttribute('data-sc-theme', t);
 *     r.classList.remove('sc-theme-pending');
 *     r.classList.add('sc-theme-ready');
 *   })();
 * </script>
 * <script src="{{ 'teset-theme.js' | asset_url }}" defer></script>
 */
(function () {
  const STORAGE_KEY = 'sc-theme';
  const ROOT = document.documentElement;
  const LIGHT = 'light';
  const DARK = 'dark';
  const GAP = 10;

  const SCHEME_LIGHT = 'color-scheme-1';
  const SCHEME_DARK = 'color-scheme-5';
  const FORM_STYLES_ID = 'sc-shopify-forms-theme';
  const B2B_SECTION_ID = 'shopify-section-template--19456256770201__1782219431add1cd53';

  let toggleBtn = null;
  let themeApplied = false;
  let alignQueued = false;
  let formsObserverStarted = false;

  function getPreferredTheme() {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === LIGHT || saved === DARK) return saved;
    return LIGHT;
  }

  function swapHorizonSchemeClasses(fromClass, toClass) {
    document.querySelectorAll('.' + fromClass).forEach(function (el) {
      el.classList.remove(fromClass);
      el.classList.add(toClass);
    });
  }

  function applyHorizonSchemeClasses(theme) {
    if (theme === DARK) {
      swapHorizonSchemeClasses(SCHEME_LIGHT, SCHEME_DARK);
    } else {
      swapHorizonSchemeClasses(SCHEME_DARK, SCHEME_LIGHT);
    }
  }

  function updateToggleAria(btn, theme) {
    btn.setAttribute('aria-pressed', theme === DARK ? 'true' : 'false');
    btn.setAttribute(
      'aria-label',
      theme === DARK ? 'Switch to light theme' : 'Switch to dark theme'
    );
  }

  function setTheme(theme, persist) {
    const next = theme === DARK ? DARK : LIGHT;
    ROOT.setAttribute('data-sc-theme', next);
    ROOT.classList.add('sc-theme-ready');
    ROOT.classList.remove('sc-theme-pending');

    if (persist) {
      localStorage.setItem(STORAGE_KEY, next);
    }

    applyHorizonSchemeClasses(next);

    syncAllShopifyForms(next);

    if (toggleBtn) {
      updateToggleAria(toggleBtn, next);
    }
  }

  function getFormStyleRules(theme) {
    const isDark = theme === DARK;

    if (isDark) {
      return (
        ':host{' +
        '--body-text-color:#555555!important;' +
        '--heading-text-color:#c5a155!important;' +
        '--form-text-color:#f2f2f2!important;' +
        '--form-placeholder-color:rgb(242 242 242 / 0.7)!important;' +
        '--form-background-color:#2c2c2c!important;' +
        '--form-field-border:none!important;' +
        '--form-field-border-focused:none!important;' +
        '}' +
        '[class*="textHeading"],h1,h2,h3{color:#c5a155!important;}' +
        '[class*="textBody"]{color:#ffffff!important;}' +
        '[class*="formHeader"]{text-align:center!important;}' +
        '[class*="formFieldContainer"]:has(textarea)::before{background-color:#2c2c2c!important;}' +
        'button[type="submit"],[class*="SubmitButton"],[class*="submitButton"]{' +
        'background-color:#c5a155!important;color:#ffffff!important;border:none!important;' +
        'text-transform:uppercase!important;letter-spacing:0.15em!important;}' +
        'button[type="submit"]:hover,[class*="SubmitButton"]:hover,[class*="submitButton"]:hover{' +
        'background-color:#b8924a!important;}'
      );
    }

    return (
      ':host{' +
      '--body-text-color:#555555!important;' +
      '--heading-text-color:#c5a155!important;' +
      '--form-text-color:#1a1a1a!important;' +
      '--form-placeholder-color:rgb(26 26 26 / 0.45)!important;' +
      '--form-background-color:#f4f4f4!important;' +
      '--form-field-border:0 0 0 1px #e8e8e8!important;' +
      '--form-field-border-focused:none!important;' +
      '}' +
      '[class*="textHeading"],h1,h2,h3{color:#c5a155!important;}' +
      '[class*="_label_"]{color:#555555!important;}' +
      '[class*="formHeader"]{text-align:center!important;}' +
      '[class*="formFieldContainer"]:has(textarea)::before{background-color:#f4f4f4!important;}' +
      'button[type="submit"],[class*="SubmitButton"],[class*="submitButton"]{' +
      'background-color:#c5a155!important;color:#ffffff!important;border:none!important;' +
      'text-transform:uppercase!important;letter-spacing:0.15em!important;}'
    );
  }

  function ensureFormStylesMap(theme) {
    window.formStyles = window.formStyles || new Map();
    window.formStyles.set(FORM_STYLES_ID, getFormStyleRules(theme));
  }

  function findFormEmbeds() {
    const embeds = new Set();
    document.querySelectorAll('shopify-forms-embed, form-embed').forEach(function (el) {
      embeds.add(el);
    });
    document.querySelectorAll('[data-forms-id]').forEach(function (container) {
      container.querySelectorAll('shopify-forms-embed, form-embed').forEach(function (el) {
        embeds.add(el);
      });
    });
    return embeds;
  }

  const themeSheets = new WeakMap();

  function injectFormStylesIntoShadow(shadowRoot, theme) {
    if (!shadowRoot) return;

    const css = getFormStyleRules(theme);

    try {
      let sheet = themeSheets.get(shadowRoot);
      if (!sheet) {
        sheet = new CSSStyleSheet();
        themeSheets.set(shadowRoot, sheet);
      }
      sheet.replaceSync(css);
      const existing = Array.from(shadowRoot.adoptedStyleSheets || []);
      if (!existing.includes(sheet)) {
        shadowRoot.adoptedStyleSheets = existing.concat(sheet);
      }
    } catch (e) {
      // adoptedStyleSheets not supported — fall back to <style>
    }

    let style = shadowRoot.getElementById(FORM_STYLES_ID);
    if (!style) {
      style = document.createElement('style');
      style.id = FORM_STYLES_ID;
    }
    style.textContent = css;
    shadowRoot.appendChild(style);
  }

  function syncShopifyFormsTheme(theme) {
    const isDark = theme === DARK;
    const roots = document.querySelectorAll('[data-form-root="true"]');

    roots.forEach(function (root) {
      root.setAttribute('data-forms-text-color', isDark ? '#f2f2f2' : '#1a1a1a');
      root.setAttribute('data-forms-button-background-color', '#c5a155');
      root.setAttribute('data-forms-button-label-color', '#ffffff');
      root.setAttribute('data-forms-links-color', isDark ? '#c5a155' : '#a88642');
      root.setAttribute('data-forms-errors-color', '#e02229');
      root.setAttribute('data-forms-text-alignment', 'left');
      root.setAttribute('data-forms-alignment', 'center');
    });
  }

  function syncAllShopifyForms(theme) {
    ensureFormStylesMap(theme);
    syncShopifyFormsTheme(theme);

    findFormEmbeds().forEach(function (embed) {
      injectFormStylesIntoShadow(embed.shadowRoot, theme);
    });

    document.querySelectorAll('[data-forms-id] *').forEach(function (el) {
      if (el.shadowRoot) {
        injectFormStylesIntoShadow(el.shadowRoot, theme);
      }
    });
  }

  function observeShopifyForms() {
    if (formsObserverStarted) return;
    formsObserverStarted = true;

    const resync = function () {
      const theme = ROOT.getAttribute('data-sc-theme') === DARK ? DARK : LIGHT;
      syncAllShopifyForms(theme);
      checkB2bFormReady();
    };

    const observer = new MutationObserver(resync);
    observer.observe(document.documentElement, { childList: true, subtree: true });

    window.addEventListener('load', resync);
    document.addEventListener('shopify:section:load', resync);
    window.setTimeout(resync, 100);
    window.setTimeout(resync, 600);
    window.setTimeout(resync, 2000);
    window.setTimeout(resync, 5000);
  }

  function getB2bFormRoot() {
    const section = document.getElementById(B2B_SECTION_ID);
    if (!section) return null;
    return section.querySelector('[data-form-root="true"]');
  }

  function isB2bFormRendered(root) {
    const embed = root.querySelector('shopify-forms-embed, form-embed');
    if (!embed || !embed.shadowRoot) return false;
    return !!embed.shadowRoot.querySelector(
      'input, textarea, [class*="formInputField"], [class*="selectToggle"]'
    );
  }

  function checkB2bFormReady() {
    const root = getB2bFormRoot();
    if (!root || !root.classList.contains('sc-b2b-form--loading')) return;

    if (isB2bFormRendered(root)) {
      root.classList.remove('sc-b2b-form--loading');
      root.removeAttribute('aria-busy');
    }
  }

  function setupB2bFormLoader() {
    const root = getB2bFormRoot();
    if (!root) return;

    if (!root.querySelector('.sc-b2b-form__loader')) {
      root.classList.add('sc-b2b-form--loading');
      root.setAttribute('aria-busy', 'true');

      const loader = document.createElement('div');
      loader.className = 'sc-b2b-form__loader';
      loader.setAttribute('aria-live', 'polite');
      loader.innerHTML =
        '<span class="sc-b2b-form__spinner" aria-hidden="true"></span>' +
        '<span class="sc-b2b-form__loader-text">Loading</span>';
      root.prepend(loader);
    }

    checkB2bFormReady();
  }

  function getCollectionHandleFromPath() {
    const match = window.location.pathname.match(/\/collections\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function getCollectionHandleFromHref(href) {
    if (!href) return null;
    try {
      const path = new URL(href, window.location.origin).pathname;
      const match = path.match(/\/collections\/([^/?#]+)/);
      return match ? match[1] : null;
    } catch (e) {
      return null;
    }
  }

  function markActiveSbcItem() {
    const handle = getCollectionHandleFromPath();
    if (!handle) return;

    document.querySelectorAll('.sbc-item').forEach(function (item) {
      const itemHandle = getCollectionHandleFromHref(item.getAttribute('href'));
      const isActive = itemHandle === handle;

      item.classList.toggle('sbc-item--active', isActive);

      if (isActive) {
        item.setAttribute('aria-current', 'page');
        item.setAttribute('aria-disabled', 'true');
        item.setAttribute('tabindex', '-1');
      } else {
        item.removeAttribute('aria-current');
        item.removeAttribute('aria-disabled');
        item.removeAttribute('tabindex');
      }
    });
  }

  function applyThemeOnce() {
    if (themeApplied) return;
    themeApplied = true;
    setTheme(getPreferredTheme(), false);
  }

  function toggleTheme() {
    const current = ROOT.getAttribute('data-sc-theme') === DARK ? DARK : LIGHT;
    setTheme(current === DARK ? LIGHT : DARK, true);
  }

  function findIconsRow() {
    return (
      document.querySelector('.header__icons') ||
      document.querySelector('.header-actions') ||
      document.querySelector('.header__actions') ||
      // Horizon's header right column. None of the three class names above
      // exist in this theme - the real element is the <header-actions> TAG -
      // so without this the mount always failed and the button fell back to
      // position:fixed, landing on top of the search and cart icons on phones.
      document.querySelector('.header__column--right')
    );
  }

  function createToggleButton() {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sc-theme-toggle';
    btn.setAttribute('aria-pressed', 'false');
    btn.setAttribute('aria-label', 'Toggle color theme');

    const iconLight = document.createElement('span');
    iconLight.className = 'sc-theme-toggle__icon sc-theme-toggle__icon--light';
    iconLight.setAttribute('aria-hidden', 'true');

    const iconDark = document.createElement('span');
    iconDark.className = 'sc-theme-toggle__icon sc-theme-toggle__icon--dark';
    iconDark.setAttribute('aria-hidden', 'true');

    btn.append(iconLight, iconDark);
    btn.addEventListener('click', toggleTheme);
    return btn;
  }

  function clearInlinePosition(btn) {
    btn.style.position = '';
    btn.style.top = '';
    btn.style.right = '';
    btn.style.left = '';
    btn.style.bottom = '';
    btn.style.transform = '';
    btn.style.zIndex = '';
  }

  function alignPinnedToggle() {
    if (!toggleBtn || !toggleBtn.classList.contains('sc-theme-toggle--pinned')) return;

    const icons = findIconsRow();
    toggleBtn.style.position = 'fixed';
    toggleBtn.style.left = 'auto';
    toggleBtn.style.bottom = 'auto';
    toggleBtn.style.zIndex = '2147483646';

    if (!icons) {
      toggleBtn.style.top = '1rem';
      toggleBtn.style.right = '1rem';
      toggleBtn.style.transform = 'none';
      return;
    }

    const rect = icons.getBoundingClientRect();

    if (rect.width < 4 || rect.height < 4 || rect.left < window.innerWidth * 0.35) {
      toggleBtn.style.top = '1rem';
      toggleBtn.style.right = '1rem';
      toggleBtn.style.transform = 'none';
      return;
    }

    toggleBtn.style.top = rect.top + rect.height / 2 + 'px';
    toggleBtn.style.transform = 'translateY(-50%)';
    toggleBtn.style.right = window.innerWidth - rect.left + GAP + 'px';
  }

  function scheduleAlign() {
    if (alignQueued) return;
    alignQueued = true;
    requestAnimationFrame(function () {
      alignQueued = false;
      alignPinnedToggle();
    });
  }

  function tryMountInHeader() {
    const icons = findIconsRow();
    if (!icons) return false;

    toggleBtn.classList.remove('sc-theme-toggle--pinned');
    clearInlinePosition(toggleBtn);

    if (!icons.contains(toggleBtn)) {
      // append, not prepend: keeps the order search, cart, toggle, which is
      // how it already looked while the button was floating at the top right
      icons.append(toggleBtn);
    }

    return true;
  }

  function mountPinnedFallback() {
    toggleBtn.classList.add('sc-theme-toggle--pinned');

    if (toggleBtn.parentElement !== ROOT) {
      ROOT.appendChild(toggleBtn);
    }

    scheduleAlign();
  }

  function ensureToggleButton() {
    if (!toggleBtn || !toggleBtn.isConnected) {
      toggleBtn = createToggleButton();
      updateToggleAria(
        toggleBtn,
        ROOT.getAttribute('data-sc-theme') === DARK ? DARK : LIGHT
      );
    }
  }

  function mountToggle() {
    ensureToggleButton();

    if (tryMountInHeader()) {
      return toggleBtn;
    }

    mountPinnedFallback();
    return toggleBtn;
  }

  function bindLayoutListeners() {
    window.addEventListener('resize', scheduleAlign, { passive: true });
    window.addEventListener('scroll', scheduleAlign, { passive: true });
    window.addEventListener('load', mountToggle);
    document.addEventListener('shopify:section:load', mountToggle);
  }

  function startMountGuard() {
    window.setInterval(function () {
      const existing = document.querySelector('.sc-theme-toggle');

      if (!existing) {
        toggleBtn = null;
        mountToggle();
        return;
      }

      toggleBtn = existing;

      const icons = findIconsRow();
      if (icons && !icons.contains(toggleBtn)) {
        mountToggle();
      } else if (toggleBtn.classList.contains('sc-theme-toggle--pinned')) {
        scheduleAlign();
      }
    }, 1500);
  }

  function init() {
    setupB2bFormLoader();
    markActiveSbcItem();
    applyThemeOnce();
    observeShopifyForms();
    mountToggle();
    bindLayoutListeners();
    startMountGuard();

    window.setTimeout(mountToggle, 100);
    window.setTimeout(mountToggle, 600);
    window.setTimeout(mountToggle, 2000);

    document.addEventListener('shopify:section:load', markActiveSbcItem);
  }

  const bootTheme =
    ROOT.getAttribute('data-sc-theme') === DARK ||
    localStorage.getItem(STORAGE_KEY) === DARK
      ? DARK
      : LIGHT;
  ensureFormStylesMap(bootTheme);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
